import { Badge } from "@frontend/components/ui/badge"
import { Button } from "@frontend/components/ui/button"
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger
} from "@frontend/components/ui/context-menu"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@frontend/components/ui/resizable"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@frontend/components/ui/tabs"
import { useApiClient, useApiQuery } from "@frontend/hooks/use-api"
import { extractValidationErrors } from "@frontend/lib/api-error"
import { AnnotationTimeStatusBar } from "@frontend/pages/case-workspace/components/annotation-time-status-bar"
import { CaseInfoPanel } from "@frontend/pages/case-workspace/components/case-info-panel"
import { ClinicalHistoryPanel } from "@frontend/pages/case-workspace/components/clinical-history-panel"
import { HistoryTimeline } from "@frontend/pages/case-workspace/components/history-timeline"
import { ReviewActionBar } from "@frontend/pages/case-workspace/components/review-action-bar"
import { StatusBar } from "@frontend/pages/case-workspace/components/status-bar"
import { useAnnotationHistoryBridge } from "@frontend/pages/case-workspace/hooks/use-annotation-history-bridge"
import { useModuleCompletionWatcher } from "@frontend/pages/case-workspace/hooks/use-module-completion-watcher"
import { isAmbientModuleType } from "@frontend/pages/case-workspace/lib/ambient-modules"
import {
  getModuleCompletionMap,
  getModuleDependencyLockMap
} from "@frontend/pages/case-workspace/lib/module-dependency-lock"
import { sortModulesByDependencyOrder } from "@frontend/pages/case-workspace/lib/module-dependency-order"
import { deriveEarliestStudyDate } from "@frontend/pages/case-workspace/lib/study-date"
import { validateModuleCompletion } from "@frontend/pages/case-workspace/lib/validate-module-completion"
import { setDraggedSeriesId } from "@frontend/pages/case-workspace/lib/viewer-dnd"
import { HistoViewerToolbar } from "@frontend/pages/case-workspace/pathology/components/histo-viewer-toolbar"
import { PathologyModuleSidebar } from "@frontend/pages/case-workspace/pathology/components/module-panels"
import {
  flattenVectorLabels,
  parseModuleSchema,
  resolveVectorGroups
} from "@frontend/pages/case-workspace/pathology/components/module-schema"
import { PresenceCursors } from "@frontend/pages/case-workspace/pathology/components/presence-cursors"
import { RadialToolSelector } from "@frontend/pages/case-workspace/pathology/components/radial-tool-selector"
import { SupportingMaterialViewer } from "@frontend/pages/case-workspace/pathology/components/supporting-material-viewer"
import { VoiceNoteGhostCursor } from "@frontend/pages/case-workspace/pathology/components/voice-note-ghost-cursor"
import { VoiceNotesPanel } from "@frontend/pages/case-workspace/pathology/components/voice-notes-panel"
import { useAudioInputDevices } from "@frontend/pages/case-workspace/pathology/hooks/use-audio-input-devices"
import { useEditRegions } from "@frontend/pages/case-workspace/pathology/hooks/use-edit-regions"
import { useLabelRasterEditing } from "@frontend/pages/case-workspace/pathology/hooks/use-label-raster-editing"
import { useOverlayPeek } from "@frontend/pages/case-workspace/pathology/hooks/use-overlay-peek"
import { usePathologySession } from "@frontend/pages/case-workspace/pathology/hooks/use-pathology-session"
import { usePathologyViewer } from "@frontend/pages/case-workspace/pathology/hooks/use-pathology-viewer"
import { usePresence } from "@frontend/pages/case-workspace/pathology/hooks/use-presence"
import { useVoiceNotePlayback } from "@frontend/pages/case-workspace/pathology/hooks/use-voice-note-playback"
import { useVoiceNotePrompt } from "@frontend/pages/case-workspace/pathology/hooks/use-voice-note-prompt"
import {
  useVoiceNoteRecorder,
  type VoiceNoteListItem,
  voiceNotesQueryKey
} from "@frontend/pages/case-workspace/pathology/hooks/use-voice-note-recorder"
import { useWsiOverlays } from "@frontend/pages/case-workspace/pathology/hooks/use-wsi-overlays"
import { getModuleEditRegionDomain } from "@frontend/pages/case-workspace/pathology/lib/module-edit-regions"
import {
  getActiveLabelRasterDomain,
  getLabelRasterModuleDomains
} from "@frontend/pages/case-workspace/pathology/lib/overlay-module-visibility"
import type {
  CaseDetails,
  CaseSeriesStudyDate,
  CaseSupportingMaterial,
  LabelRasterTool,
  ProjectDetails,
  ProjectModulesResponse,
  SeriesEditBbox,
  VectorTool,
  WsiSource
} from "@frontend/pages/case-workspace/pathology/types"
import { useAnnotationStore } from "@frontend/stores/annotation-store"
import { useHistoryStore } from "@frontend/stores/history-store"
import { useQueryClient } from "@tanstack/react-query"
import { ArrowLeft, ChevronLeft, ChevronRight, Layers, Mic, Pencil, Redo2, Save, Send, Undo2 } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { usePanelRef } from "react-resizable-panels"
import { useNavigate, useParams } from "react-router"
import { toast } from "sonner"

function PullTab({ direction, onClick }: { direction: "left" | "right"; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="h-full w-full flex items-center justify-center hover:bg-accent/40 transition-colors"
      title={direction === "right" ? "Expand series panel" : "Expand module panel"}
    >
      {direction === "right" ? (
        <ChevronRight className="size-3.5 text-muted-foreground" />
      ) : (
        <ChevronLeft className="size-3.5 text-muted-foreground" />
      )}
    </button>
  )
}

interface PathologyViewerPageProps {
  reviewSubmissionId?: string | null
}

export function PathologyViewerPage({ reviewSubmissionId = null }: PathologyViewerPageProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const api = useApiClient()
  const { projectId, caseId } = useParams<{
    projectId: string
    caseId: string
  }>()

  const [activeSeriesId, setActiveSeriesId] = useState<string | null>(null)
  const [isSeriesDragging, setIsSeriesDragging] = useState(false)
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null)
  const [rightRailTab, setRightRailTab] = useState<"modules" | "voice">("modules")
  const [activeVectorLabelKey, setActiveVectorLabelKey] = useState<string | null>(null)
  const [activeVectorTool, setActiveVectorTool] = useState<VectorTool | null>(null)
  const [vectorEraserRadiusPx, setVectorEraserRadiusPx] = useState(24)
  const [vectorBrushRadiusPx, setVectorBrushRadiusPx] = useState(24)
  const [leftCollapsed, setLeftCollapsed] = useState(false)
  const [rightCollapsed, setRightCollapsed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [moduleCompletionError, setModuleCompletionError] = useState<{
    moduleId: string
    errors: string[]
  } | null>(null)
  const [activeTab, setActiveTab] = useState("viewer")
  const [labelRasterTool, setLabelRasterTool] = useState<LabelRasterTool | null>(null)
  const [labelRasterClassId, setLabelRasterClassId] = useState<number | null>(null)
  const [labelRasterRadiusPx, setLabelRasterRadiusPx] = useState(24)
  // Overwrite mode is a UI setting for this session only (never saved with the annotation).
  const [labelRasterOverwrite, setLabelRasterOverwrite] = useState(false)
  const leftPanelRef = usePanelRef()
  const rightPanelRef = usePanelRef()

  const hasHistorySession = useHistoryStore((s) => s.session !== null)
  const historyCursor = useHistoryStore((s) => s.session?.cursor ?? 0)
  const historyCheckpointCount = useHistoryStore((s) => s.session?.checkpoints.length ?? 0)
  const vectors = useAnnotationStore((s) => s.vectors)
  const scores = useAnnotationStore((s) => s.scores)
  const imageLabels = useAnnotationStore((s) => s.imageLabels)
  const texts = useAnnotationStore((s) => s.texts)
  const moduleCompletions = useAnnotationStore((s) => s.moduleCompletions)
  const labelRasters = useAnnotationStore((s) => s.labelRasters)
  const replaceVectorsForModule = useAnnotationStore((s) => s.replaceVectorsForModule)
  const upsertScore = useAnnotationStore((s) => s.upsertScore)
  const upsertImageLabel = useAnnotationStore((s) => s.upsertImageLabel)
  const upsertText = useAnnotationStore((s) => s.upsertText)
  const setModuleCompletion = useAnnotationStore((s) => s.setModuleCompletion)

  const { data: currentUser } = useApiQuery<{ instance_role: "admin" | "member" | null }>(["auth", "me"], "/auth/me")

  const { data: caseData } = useApiQuery<CaseDetails>(["cases", caseId!, "detail"], `/cases/${caseId}`, {
    enabled: Boolean(caseId)
  })
  const { data: projectData } = useApiQuery<ProjectDetails>(
    ["projects", projectId!, "detail"],
    `/projects/${projectId}`,
    { enabled: Boolean(projectId) }
  )
  const { data: caseSeriesData } = useApiQuery<{ data: CaseSeriesStudyDate[] }>(
    ["cases", caseId!, "series", "study-date"],
    `/cases/${caseId}/series`,
    { enabled: Boolean(caseId) }
  )

  const { data: projectModulesData } = useApiQuery<ProjectModulesResponse>(
    ["projects", projectId!, "modules"],
    `/projects/${projectId}/modules`,
    { enabled: Boolean(projectId) }
  )

  const { data: wsiSources } = useApiQuery<WsiSource[]>(
    ["cases", caseId!, "wsi-sources"],
    `/cases/${caseId}/wsi/sources`,
    { enabled: Boolean(caseId) }
  )
  const { data: caseMaterials } = useApiQuery<CaseSupportingMaterial[]>(
    ["cases", caseId!, "supporting-materials"],
    `/cases/${caseId}/supporting-materials`,
    { enabled: Boolean(caseId) }
  )
  // Edit bounding boxes (label_raster editing confinement). An empty list from the
  // server = unrestricted; while the request is pending or failed the boxes are
  // unknown and label raster editing stays disabled (fail closed).
  const editBboxesQuery = useApiQuery<{ data: SeriesEditBbox[] }>(
    ["cases", caseId!, "edit-bboxes"],
    `/cases/${caseId}/edit-bboxes`,
    { enabled: Boolean(caseId) }
  )
  const editBboxesData = editBboxesQuery.data
  const hasSupportingMaterials = Boolean(caseMaterials?.length)
  const studyDate = useMemo(() => deriveEarliestStudyDate(caseSeriesData?.data ?? []), [caseSeriesData])

  const canViewTimeline =
    currentUser?.instance_role === "admin" ||
    projectData?.current_user_role === "instance_admin" ||
    projectData?.current_user_role === "project_admin" ||
    projectData?.current_user_role === "reviewer"

  const projectModules = useMemo(
    () => sortModulesByDependencyOrder(projectModulesData?.data ?? []),
    [projectModulesData]
  )
  const interactiveModules = useMemo(
    () => projectModules.filter((m) => !isAmbientModuleType(m.template_type)),
    [projectModules]
  )
  const hasAnnotationTimeModule = projectModules.some((m) => m.template_type === "annotation_time")
  const voiceNoteModule = useMemo(
    () => projectModules.find((m) => m.template_type === "voice_note") ?? null,
    [projectModules]
  )
  const moduleCompletionMap = useMemo(() => getModuleCompletionMap(moduleCompletions), [moduleCompletions])
  const moduleLockById = useMemo(
    () => getModuleDependencyLockMap(projectModules, moduleCompletionMap),
    [moduleCompletionMap, projectModules]
  )

  const activeModule = useMemo(
    () => interactiveModules.find((m) => m.id === activeModuleId) ?? interactiveModules[0] ?? null,
    [activeModuleId, interactiveModules]
  )
  const activeModuleLock = useMemo(
    () => (activeModule ? moduleLockById.get(activeModule.id) : null),
    [activeModule, moduleLockById]
  )
  const baseModuleEditable = activeModuleLock?.isEditable ?? true
  const activeModuleLockReason = activeModuleLock?.lockReason ?? null

  useEffect(() => {
    if (!activeModuleId && interactiveModules[0]) {
      setActiveModuleId(interactiveModules[0].id)
    }
  }, [activeModuleId, interactiveModules])

  const baseSources = useMemo(() => (wsiSources ?? []).filter((s) => s.role !== "overlay"), [wsiSources])

  useEffect(() => {
    if (!activeSeriesId && baseSources.length) {
      setActiveSeriesId(baseSources[0]!.id)
    }
  }, [activeSeriesId, baseSources])

  useEffect(() => {
    setActiveTab("viewer")
  }, [])

  useEffect(() => {
    if (activeModule?.template_type !== "vector" || !baseModuleEditable) {
      setActiveVectorLabelKey(null)
      setActiveVectorTool(null)
    }
  }, [activeModule?.template_type, baseModuleEditable])

  const activeModuleSchema = useMemo(
    () => (activeModule ? parseModuleSchema(activeModule.template_schema) : null),
    [activeModule]
  )

  // Overlay visibility follows the active label_raster module (see useWsiOverlays).
  const labelRasterDomains = useMemo(() => getLabelRasterModuleDomains(interactiveModules), [interactiveModules])
  const activeLabelRasterDomain = useMemo(
    () => getActiveLabelRasterDomain(activeModule, activeModuleSchema),
    [activeModule, activeModuleSchema]
  )

  // Edit regions: which of the active slide's boxes confine the active module.
  const activeSeriesEditBboxes = useMemo(() => {
    if (!activeSeriesId) return null
    if (!editBboxesData) return
    return editBboxesData.data.filter((bbox) => bbox.series_id === activeSeriesId)
  }, [editBboxesData, activeSeriesId])
  const activeEditRegionDomain = useMemo(() => getModuleEditRegionDomain(activeModule), [activeModule])
  // Fail closed: no editing tool until the server has said whether this slide has boxes.
  const editRegionsBlocked = activeEditRegionDomain !== null && activeSeriesEditBboxes === undefined

  const activeVectorGroups = useMemo(() => {
    if (activeModule?.template_type !== "vector") return []
    if (activeModuleSchema?.type !== "vector") return []
    return resolveVectorGroups(activeModuleSchema)
  }, [activeModule?.template_type, activeModuleSchema])

  const activeVectorLabels = useMemo(() => flattenVectorLabels(activeVectorGroups), [activeVectorGroups])

  const isActiveModuleComplete = useMemo(
    () => moduleCompletions.find((c) => c.projectModuleId === activeModule?.id)?.completed ?? false,
    [activeModule?.id, moduleCompletions]
  )

  const {
    submissionId,
    getAuthHeaders,
    applySnapshot,
    handleTimelineScrub,
    uploadHistoryNow,
    isHistoryDirty,
    isUploadingHistory,
    checkpointCount,
    isBootstrappingSession,
    markActiveModuleComplete,
    setModuleCompletionRemote,
    isReviewMode,
    reviewEditEnabled,
    reviewSubmission,
    reviewStatus,
    setReviewStatus,
    setReviewEditEnabled,
    refetchReviewSubmission,
    isViewOnlyMode,
    startAnnotating
  } = usePathologySession({
    api,
    caseId,
    projectId,
    activeModuleId: activeModule?.id ?? null,
    isActiveModuleComplete,
    setModuleCompletion,
    reviewSubmissionId,
    currentUserRole: projectData?.current_user_role
  })

  const isActiveModuleEditable = baseModuleEditable && (!isReviewMode || reviewEditEnabled) && !isViewOnlyMode

  useAnnotationHistoryBridge(!isBootstrappingSession && hasHistorySession)

  useModuleCompletionWatcher({
    modules: interactiveModules,
    moduleCompletions,
    scores,
    vectors,
    imageLabels,
    texts,
    labelRasters,
    setModuleCompletionRemote,
    isReadOnly: isBootstrappingSession || isViewOnlyMode || (isReviewMode && !reviewEditEnabled)
  })

  const presenceEnabled = projectData?.multiplayer_cursors === 1

  const {
    viewerRef,
    viewerEpoch,
    viewerContainerRef,
    canvasRef,
    viewerStatus,
    activeModuleVectors,
    activeVectorLabelByKey,
    vectorOpacityDraftById,
    selectedAnnotationId,
    handleSelectAnnotation,
    handleVectorVisibilityToggle,
    handleVectorDelete,
    handleVectorOpacityChange,
    handleVectorOpacityCommit,
    hiddenGroupKeys,
    handleToggleGroupVisibility,
    isViewerDropTarget
  } = usePathologyViewer({
    caseId,
    wsiSources,
    activeSeriesId,
    activeModule,
    activeVectorLabelKey: isActiveModuleEditable ? activeVectorLabelKey : null,
    activeVectorTool,
    vectorEraserRadiusPx,
    vectorBrushRadiusPx,
    activeVectorLabels,
    activeVectorGroups,
    vectors,
    replaceVectorsForModule,
    getAuthHeaders,
    isActiveModuleEditable,
    isSeriesDragging,
    onSeriesDrop: (seriesId) => setActiveSeriesId(seriesId),
    onVectorEraserRadiusChange: setVectorEraserRadiusPx,
    onVectorBrushRadiusChange: setVectorBrushRadiusPx,
    onVectorToolChange: setActiveVectorTool,
    labelRasterTool,
    vectorEditingBlocked: editRegionsBlocked
  })

  const maxToolRadius = useMemo(() => Math.round((viewerStatus?.imageWidth ?? 2048) * 0.25), [viewerStatus?.imageWidth])

  const editRegions = useEditRegions({
    viewerRef,
    viewerEpoch,
    domain: activeEditRegionDomain,
    editBboxes: activeSeriesEditBboxes
  })

  const overlays = useWsiOverlays({
    viewerRef,
    viewerEpoch,
    wsiSources,
    activeSeriesId,
    labelRasterDomains,
    activeLabelRasterDomain
  })

  const labelRaster = useLabelRasterEditing({
    viewerRef,
    viewerEpoch,
    activeModule,
    activeModuleSchema,
    activeSeriesId,
    wsiSources,
    overlayLayers: overlays.layers,
    setOverlayLabelOpacity: overlays.setLabelOpacity,
    isActiveModuleEditable,
    tool: labelRasterTool,
    onToolChange: setLabelRasterTool,
    classId: labelRasterClassId,
    onClassChange: setLabelRasterClassId,
    radiusPx: labelRasterRadiusPx,
    onRadiusChange: setLabelRasterRadiusPx,
    maxRadiusPx: maxToolRadius,
    overwrite: labelRasterOverwrite,
    onOverwriteChange: setLabelRasterOverwrite,
    editRegionsBlocked,
    editBboxesError: editBboxesQuery.isError
  })

  // Hold Space to peek at the tissue under the overlay; scoped to the active label_raster module.
  useOverlayPeek({ viewerRef, viewerEpoch, enabled: activeLabelRasterDomain !== null })

  const { peers, peersRef } = usePresence({
    projectId,
    caseId,
    viewerRef,
    enabled: presenceEnabled,
    isViewerReady: Boolean(viewerStatus)
  })

  // ── Voice notes (ambient module: Voice tab in the right rail, every case reader may play) ──
  const hasVoiceNotes = Boolean(voiceNoteModule)
  const canRecordVoiceNote =
    hasVoiceNotes && Boolean(submissionId) && !isReviewMode && !isViewOnlyMode && !isBootstrappingSession
  const voiceRecordDisabledReason = !hasVoiceNotes
    ? null
    : isReviewMode
      ? "Review mode: play the annotator's notes; recording is available on your own submission."
      : isViewOnlyMode
        ? "View only: start annotating to record voice notes."
        : null
  const audioInputs = useAudioInputDevices({ enabled: canRecordVoiceNote })
  const voiceRecorder = useVoiceNoteRecorder({
    viewerRef,
    viewerEpoch,
    caseId,
    submissionId,
    projectModuleId: voiceNoteModule?.id ?? null,
    activeSeriesId,
    canRecord: canRecordVoiceNote,
    deviceId: audioInputs.selectedDeviceId
  })
  // Offer a recording once per slide when the case opens (Start = the only way it begins).
  useVoiceNotePrompt({
    caseId,
    activeSeriesId,
    canRecord: canRecordVoiceNote,
    isViewerReady: Boolean(viewerStatus),
    status: voiceRecorder.status,
    unsupportedReason: voiceRecorder.unsupportedReason,
    start: voiceRecorder.start
  })
  const voicePlayback = useVoiceNotePlayback({
    viewerRef,
    canvasRef,
    viewerEpoch,
    activeSeriesId,
    getAuthHeaders,
    disabled: voiceRecorder.isRecording
  })
  const voiceNotesQuery = useApiQuery<{ data: VoiceNoteListItem[] }>(
    voiceNotesQueryKey(caseId),
    `/cases/${caseId}/annotations?module_type=voice_note`,
    { enabled: hasVoiceNotes && Boolean(caseId) }
  )
  const sourceNameById = useMemo(() => new Map(baseSources.map((s) => [s.id, s.label])), [baseSources])
  const handleDeleteVoiceNote = useCallback(
    async (note: VoiceNoteListItem) => {
      if (!window.confirm(`Delete the voice note "${note.title?.trim() || "Untitled note"}"?`)) return
      if (voicePlayback.activeNote?.id === note.id) voicePlayback.stop()
      try {
        await api(`/annotations/${note.id}`, { method: "DELETE" })
        await queryClient.invalidateQueries({ queryKey: voiceNotesQueryKey(caseId) })
      } catch (error) {
        toast.error("Could not delete the voice note", {
          description: error instanceof Error ? error.message : undefined
        })
      }
    },
    [api, caseId, queryClient, voicePlayback]
  )

  const handleUpsertScore = useCallback(
    (...args: Parameters<typeof upsertScore>) => {
      if (!isActiveModuleEditable) return
      upsertScore(...args)
    },
    [isActiveModuleEditable, upsertScore]
  )

  const handleUpsertImageLabel = useCallback(
    (...args: Parameters<typeof upsertImageLabel>) => {
      if (!isActiveModuleEditable) return
      upsertImageLabel(...args)
    },
    [isActiveModuleEditable, upsertImageLabel]
  )

  const handleUpsertText = useCallback(
    (...args: Parameters<typeof upsertText>) => {
      if (!isActiveModuleEditable) return
      upsertText(...args)
    },
    [isActiveModuleEditable, upsertText]
  )

  useEffect(() => {
    if (!activeVectorLabelKey) return
    const labelGroup = activeVectorGroups.find((g) => g.labels.some((l) => l.key === activeVectorLabelKey))
    if (labelGroup && hiddenGroupKeys.has(labelGroup.key)) {
      setActiveVectorLabelKey(null)
    }
  }, [activeVectorLabelKey, activeVectorGroups, hiddenGroupKeys])

  const handleSelectVectorLabel = useCallback((labelKey: string) => {
    setActiveVectorLabelKey(labelKey)
    setActiveVectorTool((prev) => prev ?? "pen")
  }, [])

  const handleToggleVectorLabel = useCallback(
    (labelKey: string) => {
      if (!isActiveModuleEditable) return
      setActiveVectorLabelKey((curr) => (curr === labelKey ? null : labelKey))
    },
    [isActiveModuleEditable]
  )

  const handleMarkActiveModuleComplete = useCallback(async () => {
    if (!isActiveModuleEditable) return
    if (!activeModule) return

    try {
      await markActiveModuleComplete()
      setModuleCompletionError(null)
    } catch (err) {
      const errors = extractValidationErrors(err) ?? ["Failed to update completion"]
      setModuleCompletionError({ moduleId: activeModule.id, errors })
      toast.error(`Cannot mark ${activeModule.template_name} complete`, {
        description: errors.join("\n")
      })
      return
    }

    if (!isActiveModuleComplete) {
      const currentIdx = interactiveModules.findIndex((m) => m.id === activeModule.id)
      const next = interactiveModules.find(
        (m, i) => i > currentIdx && !moduleCompletions.find((c) => c.projectModuleId === m.id)?.completed
      )
      if (next) setActiveModuleId(next.id)
    }
  }, [
    isActiveModuleEditable,
    activeModule,
    markActiveModuleComplete,
    isActiveModuleComplete,
    interactiveModules,
    moduleCompletions
  ])

  // Clear the error when the user switches modules.
  useEffect(() => {
    if (moduleCompletionError && moduleCompletionError.moduleId !== activeModule?.id) {
      setModuleCompletionError(null)
    }
  }, [moduleCompletionError, activeModule?.id])

  const handleSubmit = useCallback(async () => {
    if (!submissionId) {
      setSubmitError("No active submission.")
      return
    }

    setSubmitError(null)
    setIsSubmitting(true)
    try {
      await uploadHistoryNow("submit")
      await api(`/submissions/${submissionId}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "submitted" })
      })
      await queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "cases"]
      })
      navigate(`/projects/${projectId}`)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to submit case")
    } finally {
      setIsSubmitting(false)
    }
  }, [api, navigate, projectId, queryClient, submissionId, uploadHistoryNow])

  if (!(caseId && projectId)) {
    return (
      <div className="h-screen bg-background p-6">
        <p className="text-sm text-muted-foreground">Invalid viewer route.</p>
      </div>
    )
  }

  const viewerContent = (
    <>
      <HistoViewerToolbar viewerRef={viewerRef} />
      <div
        ref={viewerContainerRef}
        className={`min-h-0 flex-1 w-full rounded-md border bg-black overflow-hidden transition-colors relative ${
          isViewerDropTarget ? "border-primary/70 ring-1 ring-primary/40" : "border-border"
        }`}
      >
        <canvas ref={canvasRef} className="h-full w-full block" />
        {presenceEnabled && (
          <PresenceCursors peersRef={peersRef} viewerRef={viewerRef} canvasRef={canvasRef} peers={peers} />
        )}
        {hasVoiceNotes && (
          <VoiceNoteGhostCursor ghostRef={voicePlayback.ghostRef} label={voicePlayback.activeNote?.title ?? null} />
        )}
        <RadialToolSelector
          containerRef={viewerContainerRef}
          activeVectorTool={activeVectorTool}
          vectorBrushRadiusPx={vectorBrushRadiusPx}
          vectorEraserRadiusPx={vectorEraserRadiusPx}
          maxToolRadius={maxToolRadius}
          onVectorToolChange={setActiveVectorTool}
          onVectorBrushRadiusChange={setVectorBrushRadiusPx}
          onVectorEraserRadiusChange={setVectorEraserRadiusPx}
          onUndo={() => applySnapshot(useHistoryStore.getState().undo())}
          onRedo={() => applySnapshot(useHistoryStore.getState().redo())}
          enabled={activeModule?.template_type === "vector" && isActiveModuleEditable}
          activeVectorGroups={activeVectorGroups}
          activeVectorLabelKey={activeVectorLabelKey}
          onSelectLabel={handleSelectVectorLabel}
        />
      </div>
      {viewerStatus && (
        <StatusBar
          cells={[
            {
              label: "Pos",
              value:
                viewerStatus.pointerImageX != null
                  ? `${Math.round(viewerStatus.pointerImageX)}, ${Math.round(viewerStatus.pointerImageY!)}`
                  : "\u2014, \u2014"
            },
            { label: "Zoom", value: `${Math.round(viewerStatus.zoom * 100)}%` },
            { label: "Level", value: String(viewerStatus.level) },
            {
              label: "Size",
              value: `${viewerStatus.imageWidth.toLocaleString()} \u00d7 ${viewerStatus.imageHeight.toLocaleString()}`
            },
            ...(overlays.cursorLabel
              ? [
                  {
                    label: "Label",
                    value: overlays.cursorLabel.name ?? `label ${overlays.cursorLabel.value}`
                  }
                ]
              : [])
          ]}
        />
      )}
      {canViewTimeline && (
        <HistoryTimeline
          checkpointCount={historyCheckpointCount}
          cursor={historyCursor}
          disabled={isBootstrappingSession || !hasHistorySession}
          onScrub={handleTimelineScrub}
        />
      )}
    </>
  )

  return (
    <div className="h-screen bg-background text-foreground flex flex-col">
      <header className="h-10 border-b border-border flex items-center px-3 gap-2 bg-card shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          aria-label="Back to project"
          onClick={() => navigate(`/projects/${projectId}`)}
        >
          <ArrowLeft className="size-3.5" />
        </Button>

        <span className="text-[12px] font-medium truncate max-w-[160px]">{caseData?.study_identifier ?? caseId}</span>
        <Badge variant="outline" className="text-[9px] shrink-0 h-4 px-1">
          {isViewOnlyMode ? "View Only" : isReviewMode ? "Review" : "In Progress"}
        </Badge>
        {!isViewOnlyMode && (
          <span className="text-[10px] text-muted-foreground shrink-0">
            {isHistoryDirty ? "Unsynced" : "Saved"} · {checkpointCount}cp
          </span>
        )}
        {voiceRecorder.isRecording && (
          <button
            type="button"
            className="flex items-center gap-1 rounded-sm bg-red-500/15 px-1.5 py-px text-[10px] font-medium text-red-400 shrink-0"
            title="Voice note recording — open the Voice tab"
            onClick={() => setRightRailTab("voice")}
          >
            <span
              className={`size-1.5 rounded-full bg-red-500 ${voiceRecorder.status === "recording" ? "animate-pulse" : ""}`}
            />
            REC {Math.floor(voiceRecorder.elapsedMs / 60000)}:
            {String(Math.floor(voiceRecorder.elapsedMs / 1000) % 60).padStart(2, "0")}
          </button>
        )}
        {submitError ? (
          <span className="text-[10px] text-destructive truncate max-w-[220px]">{submitError}</span>
        ) : null}

        <div className="ml-auto flex items-center gap-1">
          {!isViewOnlyMode && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                title="Undo (⌘Z)"
                onClick={() => applySnapshot(useHistoryStore.getState().undo())}
              >
                <Undo2 className="size-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                title="Redo (⌘⇧Z)"
                onClick={() => applySnapshot(useHistoryStore.getState().redo())}
              >
                <Redo2 className="size-3.5" />
              </Button>
              <div className="w-px h-4 bg-border mx-1" />
            </>
          )}
          {isViewOnlyMode ? (
            <Button size="sm" className="h-6 px-2 text-[11px]" onClick={() => void startAnnotating()}>
              <Pencil className="size-3 mr-1" />
              Start Annotating
            </Button>
          ) : isReviewMode ? (
            <ReviewActionBar
              submissionId={reviewSubmissionId!}
              status={reviewStatus ?? reviewSubmission?.status ?? "submitted"}
              reviewComment={reviewSubmission?.review_comment ?? null}
              editEnabled={reviewEditEnabled}
              onEditEnabledChange={setReviewEditEnabled}
              onStatusChange={(newStatus) => {
                setReviewStatus(newStatus)
                void refetchReviewSubmission()
              }}
              onSaveAndAccept={async () => {
                await uploadHistoryNow("save")
              }}
              disabled={isBootstrappingSession}
            />
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => void uploadHistoryNow("save")}
                disabled={isBootstrappingSession || isUploadingHistory || isSubmitting}
              >
                <Save className="size-3 mr-1" />
                Save
              </Button>
              <Button
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => void handleSubmit()}
                disabled={isBootstrappingSession || isUploadingHistory || isSubmitting}
              >
                <Send className="size-3 mr-1" />
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </>
          )}
        </div>
      </header>

      <ResizablePanelGroup orientation="horizontal" className="flex-1 min-h-0">
        <ResizablePanel
          panelRef={leftPanelRef}
          defaultSize={18}
          minSize={8}
          collapsible
          collapsedSize={2}
          onResize={(size) => setLeftCollapsed(size.asPercentage <= 2)}
          className="bg-card border-r border-border flex flex-col"
        >
          {leftCollapsed ? (
            <PullTab direction="right" onClick={() => leftPanelRef.current?.expand()} />
          ) : (
            <>
              <CaseInfoPanel
                projectName={projectData?.name}
                caseName={caseData?.name ?? caseData?.study_identifier}
                studyDate={studyDate}
              />
              <div className="px-2.5 py-2 text-[10px] font-medium text-muted-foreground uppercase tracking-wide border-b border-border shrink-0">
                WSI Sources
              </div>
              <div className="flex-1 overflow-auto p-2 space-y-1">
                {baseSources.map((source) => {
                  const declared = (wsiSources ?? []).filter((s) => s.role === "overlay" && s.overlayOf === source.id)
                  const isActive = activeSeriesId === source.id
                  return (
                    <div key={source.id}>
                      <ContextMenu>
                        <ContextMenuTrigger
                          render={<button type="button" />}
                          draggable
                          onDragStart={(event) => {
                            setDraggedSeriesId(event.dataTransfer, source.id)
                            event.dataTransfer.effectAllowed = "copy"
                            setIsSeriesDragging(true)
                          }}
                          onDragEnd={() => setIsSeriesDragging(false)}
                          onClick={() => setActiveSeriesId(source.id)}
                          title={source.seriesIdentifier ?? source.label}
                          className={`w-full text-left rounded-md border px-2 py-2 transition-colors ${
                            isActive
                              ? "border-primary/60 bg-primary/10"
                              : overlays.layerIds.has(source.id)
                                ? "border-border border-l-2 border-l-violet-500 bg-surface-raised hover:bg-accent/40 cursor-grab"
                                : "border-border bg-surface-raised hover:bg-accent/40 cursor-grab"
                          }`}
                        >
                          <div className="flex items-center gap-1.5">
                            <div className="text-[11px] font-medium truncate flex-1 min-w-0">{source.label}</div>
                            {declared.length > 0 && (
                              <span
                                className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground shrink-0"
                                title={`${declared.length} mask overlay${declared.length === 1 ? "" : "s"}`}
                              >
                                <Layers className="size-3" />
                                {declared.length}
                              </span>
                            )}
                          </div>
                        </ContextMenuTrigger>
                        <ContextMenuContent>
                          {source.id !== activeSeriesId && !overlays.layerIds.has(source.id) && (
                            <ContextMenuItem onClick={() => overlays.addManual(source.id)}>
                              Load as Overlay
                            </ContextMenuItem>
                          )}
                          {overlays.layerIds.has(source.id) && (
                            <ContextMenuItem variant="destructive" onClick={() => overlays.remove(source.id)}>
                              Remove Overlay
                            </ContextMenuItem>
                          )}
                        </ContextMenuContent>
                      </ContextMenu>
                    </div>
                  )
                })}
              </div>
              <ClinicalHistoryPanel clinicalHistory={caseData?.clinical_history} />
              {hasAnnotationTimeModule && <AnnotationTimeStatusBar caseId={caseId} />}
            </>
          )}
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={60} minSize={30} className="min-h-0 bg-surface-sunken">
          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              setActiveTab(value as string)
              if (value === "viewer") {
                requestAnimationFrame(() => viewerRef.current?.resize())
              }
            }}
            className="h-full flex flex-col"
          >
            {hasSupportingMaterials && (
              <TabsList className="shrink-0 mx-2 mt-2">
                <TabsTrigger value="viewer">WSI Viewer</TabsTrigger>
                <TabsTrigger value="supporting-images">Supporting Images</TabsTrigger>
              </TabsList>
            )}
            <TabsContent
              value="viewer"
              keepMounted
              className="h-full p-2 min-h-0 flex flex-col gap-2 data-[hidden]:hidden"
            >
              {viewerContent}
            </TabsContent>
            {hasSupportingMaterials && (
              <TabsContent
                value="supporting-images"
                keepMounted
                className="min-h-0 flex-1 overflow-auto p-4 data-[hidden]:hidden"
              >
                <SupportingMaterialViewer materials={caseMaterials!} caseId={caseId!} />
              </TabsContent>
            )}
          </Tabs>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel
          panelRef={rightPanelRef}
          defaultSize={22}
          minSize={10}
          collapsible
          collapsedSize={2}
          onResize={(size) => setRightCollapsed(size.asPercentage <= 2)}
          className="bg-card border-l border-border flex flex-col"
        >
          {rightCollapsed ? (
            <PullTab direction="left" onClick={() => rightPanelRef.current?.expand()} />
          ) : (
            <Tabs
              value={hasVoiceNotes ? rightRailTab : "modules"}
              onValueChange={(value) => setRightRailTab(value === "voice" ? "voice" : "modules")}
              className="h-full flex flex-col gap-0"
            >
              {hasVoiceNotes && (
                <TabsList variant="line" className="shrink-0 w-full justify-start border-b border-border px-1">
                  <TabsTrigger value="modules" className="text-[11px]">
                    Modules
                  </TabsTrigger>
                  <TabsTrigger value="voice" className="text-[11px] gap-1.5">
                    <Mic className="size-3" />
                    Voice
                    {voiceRecorder.isRecording && (
                      <span className="size-1.5 rounded-full bg-red-500 animate-pulse" aria-label="Recording" />
                    )}
                  </TabsTrigger>
                </TabsList>
              )}
              <TabsContent value="modules" keepMounted className="flex flex-1 min-h-0 flex-col data-[hidden]:hidden">
                <PathologyModuleSidebar
                  projectModules={interactiveModules}
                  moduleLockById={moduleLockById}
                  activeModule={activeModule}
                  activeModuleSchema={activeModuleSchema}
                  moduleCompletions={moduleCompletions}
                  onSelectModule={setActiveModuleId}
                  activeVectorLabelKey={activeVectorLabelKey}
                  activeVectorTool={activeVectorTool}
                  vectorEraserRadiusPx={vectorEraserRadiusPx}
                  vectorBrushRadiusPx={vectorBrushRadiusPx}
                  maxToolRadius={maxToolRadius}
                  onToggleVectorLabel={handleToggleVectorLabel}
                  onVectorToolChange={setActiveVectorTool}
                  onVectorEraserRadiusChange={setVectorEraserRadiusPx}
                  onVectorBrushRadiusChange={setVectorBrushRadiusPx}
                  activeVectorGroups={activeVectorGroups}
                  hiddenGroupKeys={hiddenGroupKeys}
                  onToggleGroupVisibility={handleToggleGroupVisibility}
                  activeModuleVectors={activeModuleVectors}
                  activeVectorLabelByKey={activeVectorLabelByKey}
                  vectorOpacityDraftById={vectorOpacityDraftById}
                  onVectorVisibilityToggle={handleVectorVisibilityToggle}
                  onVectorDelete={handleVectorDelete}
                  onVectorOpacityChange={handleVectorOpacityChange}
                  onVectorOpacityCommit={handleVectorOpacityCommit}
                  selectedAnnotationId={selectedAnnotationId}
                  onSelectAnnotation={handleSelectAnnotation}
                  activeSeriesId={activeSeriesId}
                  imageLabels={imageLabels}
                  onUpsertImageLabel={handleUpsertImageLabel}
                  scores={scores}
                  onUpsertScore={handleUpsertScore}
                  texts={texts}
                  onUpsertText={handleUpsertText}
                  isActiveModuleComplete={isActiveModuleComplete}
                  isActiveModuleEditable={isActiveModuleEditable}
                  activeModuleLockReason={activeModuleLockReason}
                  canComplete={
                    !isReviewMode && Boolean(submissionId) && Boolean(activeModule?.id) && isActiveModuleEditable
                  }
                  onMarkComplete={() => void handleMarkActiveModuleComplete()}
                  editRegions={editRegions}
                  editRegionsError={editRegionsBlocked && editBboxesQuery.isError}
                  completionErrors={
                    moduleCompletionError && moduleCompletionError.moduleId === activeModule?.id
                      ? moduleCompletionError.errors
                      : null
                  }
                  labelRaster={
                    labelRaster.schema
                      ? {
                          schema: labelRaster.schema,
                          targetOverlay: labelRaster.targetOverlay,
                          labels: labelRaster.labels,
                          unannotatedId: labelRaster.unannotatedId,
                          labelOpacities: labelRaster.labelOpacities,
                          onToggleClassHidden: labelRaster.toggleClassHidden,
                          tool: labelRasterTool,
                          onToolChange: setLabelRasterTool,
                          classId: labelRasterClassId,
                          onClassChange: setLabelRasterClassId,
                          radiusPx: labelRasterRadiusPx,
                          onRadiusChange: setLabelRasterRadiusPx,
                          maxRadiusPx: maxToolRadius,
                          overwrite: labelRasterOverwrite,
                          onOverwriteChange: setLabelRasterOverwrite,
                          overlayOpacity: labelRaster.overlayOpacity,
                          onOverlayOpacityChange: (opacity) =>
                            labelRaster.targetOverlay && overlays.setOpacity(labelRaster.targetOverlay.id, opacity),
                          stats: labelRaster.stats,
                          crcMismatchCount: labelRaster.crcMismatchCount,
                          onResetEdits: labelRaster.resetEdits,
                          readOnly: !isActiveModuleEditable,
                          errorMessage: labelRaster.errorMessage,
                          regions: editRegions.regions,
                          regionsHidden: editRegions.hidden,
                          onRegionsHiddenChange: editRegions.setHidden,
                          onJumpToRegion: editRegions.jumpToRegion,
                          onNextRegion: editRegions.nextRegion
                        }
                      : null
                  }
                />
              </TabsContent>
              {hasVoiceNotes && (
                <TabsContent value="voice" keepMounted className="flex flex-1 min-h-0 flex-col data-[hidden]:hidden">
                  <VoiceNotesPanel
                    notes={voiceNotesQuery.data?.data}
                    isLoadingNotes={voiceNotesQuery.isLoading}
                    canRecord={canRecordVoiceNote}
                    recordDisabledReason={voiceRecordDisabledReason}
                    ownSubmissionId={submissionId}
                    recorder={voiceRecorder}
                    playback={voicePlayback}
                    audioInputs={audioInputs}
                    activeSeriesId={activeSeriesId}
                    sourceNameById={sourceNameById}
                    onDelete={(note) => void handleDeleteVoiceNote(note)}
                  />
                </TabsContent>
              )}
            </Tabs>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}
